/* eslint-disable @typescript-eslint/no-explicit-any */

// TODO: Fix ESLint errors and improve type safety

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_ZOOM,
  STORAGE_KEYS,
  IP_LOCATION_CACHE_DURATION,
} from '@/utils/constants';
import type { LatLng, LatLngBounds } from '@/types/global';
import type {
  LocationPermission,
  StoredIpLocation,
} from '../types/location.types';
import { ipLocationApi } from '../api/ip-location-api';

/**
 * Simple debounce utility
 */
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Get stored IP location from localStorage if not expired
 */
function getStoredIpLocation(): StoredIpLocation | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.IP_LOCATION);
    if (!stored) return null;

    const data = JSON.parse(stored) as StoredIpLocation;
    const now = Date.now();

    // Check if expired (older than 7 days)
    if (now - data.fetchedAt > IP_LOCATION_CACHE_DURATION) {
      localStorage.removeItem(STORAGE_KEYS.IP_LOCATION);
      return null;
    }

    return data;
  } catch (error) {
    console.warn('Failed to parse stored IP location:', error);
    return null;
  }
}

/**
 * Store IP location in localStorage
 */
function storeIpLocation(
  location: LatLng,
  metadata?: Partial<StoredIpLocation>
): void {
  try {
    const data: StoredIpLocation = {
      location,
      country: metadata?.country,
      countryCode: metadata?.countryCode,
      city: metadata?.city,
      timezone: metadata?.timezone,
      fetchedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEYS.IP_LOCATION, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to store IP location:', error);
  }
}

interface LocationState {
  // State
  currentPosition: LatLng;
  mapCenter: LatLng;
  zoomLevel: number;
  visibleRegion: LatLngBounds | null;
  locationPermission: LocationPermission;
  isFetchingLocation: boolean;
  ipLocation: LatLng | null; // IP-based location
  isFetchingIpLocation: boolean;
  urlParams: {
    lat?: number;
    lng?: number;
    zoom?: number;
  };

  // Actions
  setMapCenter: (center: LatLng) => void;
  setZoomLevel: (zoom: number) => void;
  setVisibleRegion: (bounds: LatLngBounds) => void;
  setVisibleRegionImmediate: (bounds: LatLngBounds) => void;
  setLocationPermission: (permission: LocationPermission) => void;
  getCurrentLocation: () => Promise<void>;
  fetchIpLocation: () => Promise<void>;
  initializeLocation: () => Promise<void>;
  navigateToUrlLocation: () => void;
  updateUrlParams: (params: Partial<LocationState['urlParams']>) => void;
  setCurrentPosition: (position: LatLng) => void;
}

// Create the debounced setter outside the store
let debouncedSetVisibleRegion: ((bounds: LatLngBounds) => void) | null = null;

export const useLocationStore = create<LocationState>()(
  devtools(
    immer((set, get) => {
      // Create immediate setter
      const setVisibleRegionImmediate = (bounds: LatLngBounds) => {
        set({ visibleRegion: bounds });
      };

      // Create debounced version (200ms delay)
      if (!debouncedSetVisibleRegion) {
        debouncedSetVisibleRegion = debounce(setVisibleRegionImmediate, 200);
      }

      return {
        // Initial state
        currentPosition: DEFAULT_MAP_CENTER,
        mapCenter: DEFAULT_MAP_CENTER,
        zoomLevel: DEFAULT_ZOOM,
        visibleRegion: null,
        locationPermission: 'prompt',
        isFetchingLocation: false,
        ipLocation: null,
        isFetchingIpLocation: false,
        urlParams: {},

        // Set map center
        setMapCenter: (center: LatLng) => {
          set({ mapCenter: center });
        },

        // Set zoom level
        setZoomLevel: (zoom: number) => {
          set({ zoomLevel: zoom });
        },

        // Set visible region (viewport bounds) - debounced by 200ms
        setVisibleRegion: (bounds: LatLngBounds) => {
          debouncedSetVisibleRegion!(bounds);
        },

        // Set visible region immediately (no debounce)
        setVisibleRegionImmediate,

        // Set location permission
        setLocationPermission: (permission: LocationPermission) => {
          set({ locationPermission: permission });
        },

        // Get current user location
        getCurrentLocation: async () => {
          if (!navigator.geolocation) {
            console.warn('Geolocation is not supported by this browser');
            set({ locationPermission: 'disabled' });
            return;
          }

          set({ isFetchingLocation: true });

          try {
            const position = await new Promise<GeolocationPosition>(
              (resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  enableHighAccuracy: true,
                  timeout: 10000,
                  maximumAge: 0,
                });
              }
            );

            const userLocation: LatLng = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            set({
              currentPosition: userLocation,
              mapCenter: userLocation,
              zoomLevel: 15,
              locationPermission: 'granted',
              isFetchingLocation: false,
            });
          } catch (error) {
            console.error('Error getting location:', error);

            if (error instanceof GeolocationPositionError) {
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  set({ locationPermission: 'denied' });
                  break;
                case error.POSITION_UNAVAILABLE:
                  set({ locationPermission: 'disabled' });
                  break;
                case error.TIMEOUT:
                  console.warn('Geolocation request timed out');
                  break;
              }
            }

            set({ isFetchingLocation: false });
          }
        },

        // Navigate to URL location
        navigateToUrlLocation: () => {
          const { urlParams } = get();
          if (urlParams.lat && urlParams.lng) {
            set({
              mapCenter: { lat: urlParams.lat, lng: urlParams.lng },
              zoomLevel: urlParams.zoom || DEFAULT_ZOOM,
            });
          }
        },

        // Fetch IP-based location
        fetchIpLocation: async () => {
          // Check if we already have a cached IP location
          const stored = getStoredIpLocation();
          if (stored) {
            set({ ipLocation: stored.location });
            return;
          }

          // Fetch new IP location
          set({ isFetchingIpLocation: true });
          try {
            const result = await ipLocationApi.getLocationByIp();
            if (result) {
              set({ ipLocation: result.location });
              // Store in localStorage with metadata
              storeIpLocation(result.location, {
                country: result.country,
                countryCode: result.countryCode,
                city: result.city,
                timezone: result.timezone,
              });
            }
          } catch (error) {
            console.warn('Failed to fetch IP location:', error);
          } finally {
            set({ isFetchingIpLocation: false });
          }
        },

        // Initialize location with smart defaults
        // Priority: URL params > IP location > Denmark default
        initializeLocation: async () => {
          const { urlParams } = get();

          // 1. If URL params exist, use them (highest priority)
          if (urlParams.lat && urlParams.lng) {
            set({
              mapCenter: { lat: urlParams.lat, lng: urlParams.lng },
              zoomLevel: urlParams.zoom || DEFAULT_ZOOM,
            });
            return;
          }

          // 2. Check for stored IP location
          const stored = getStoredIpLocation();
          if (stored) {
            set({
              ipLocation: stored.location,
              mapCenter: stored.location,
              zoomLevel: DEFAULT_ZOOM,
            });
            return;
          }

          // 3. Try to fetch IP location
          set({ isFetchingIpLocation: true });
          try {
            const result = await ipLocationApi.getLocationByIp();
            if (result) {
              set({
                ipLocation: result.location,
                mapCenter: result.location,
                zoomLevel: DEFAULT_ZOOM,
                isFetchingIpLocation: false,
              });
              // Store in localStorage
              storeIpLocation(result.location, {
                country: result.country,
                countryCode: result.countryCode,
                city: result.city,
                timezone: result.timezone,
              });
              return;
            }
          } catch (error) {
            console.warn('Failed to fetch IP location:', error);
          } finally {
            set({ isFetchingIpLocation: false });
          }

          // 4. Fallback to Denmark default
          // (already set in initial state, no action needed)
        },

        // Update URL params
        updateUrlParams: (params: Partial<LocationState['urlParams']>) => {
          set((state) => {
            state.urlParams = { ...state.urlParams, ...params };
          });
        },

        // Set current position
        setCurrentPosition: (position: LatLng) => {
          set({ currentPosition: position });
        },
      };
    }),
    { name: 'LocationStore' }
  )
);
